// Email and identity verification on a user's profile, said separately so "verified" is never
// ambiguous. The identity checks list what we keep (status, document country and type, scores, risk
// codes, the same document on other accounts). What the document itself says is read from Didit
// only when staff press the button: shown masked, never stored, and every read is in the audit log.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IdCard, Mail, RotateCcw, ScanFace, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { humanize } from '../lib/risk';
import { useCan } from '../lib/staff';
import {
  actorLabel,
  EmptyState,
  ErrorNote,
  KV,
  Mono,
  Pill,
  SkeletonRows,
  TimeAgo,
  toast,
  ViewOnly,
} from './ui';

export interface IdCheckVerification {
  id: string;
  withdrawalId: string | null;
  /** Null, like the document fields, scores and risk codes, without users.kyc. */
  diditSessionId: string | null;
  status: string;
  documentCountry: string | null;
  documentType: string | null;
  livenessScore: number | null;
  faceMatchScore: number | null;
  warnings: string[] | null;
  resubmissions: number;
  decidedBy: string | null;
  decidedAt: number | null;
  userMessage: string | null;
  createdAt: number;
  updatedAt: number;
  sameDocument: { userId: string; username: string; status: string }[];
}

export interface IdCheck {
  approvedAt: number | null;
  validUntil: number | null;
  enabled: boolean;
  /** False: this staff member may see that checks exist and how they ended, not what they found. */
  details?: boolean;
  verifications: IdCheckVerification[];
}

interface Step {
  feature: string;
  status: string | null;
  score: number | null;
  warnings: { risk: string; level: string }[];
  matches?: number;
}

interface Details {
  status: string | null;
  documentCountry: string | null;
  documentType: string | null;
  verificationMethod: string | null;
  steps: Step[];
  document: {
    firstName: string | null;
    lastName: string | null;
    fullName: string | null;
    dateOfBirth: string | null;
    age: number | null;
    gender: string | null;
    nationality: string | null;
    issuingCountryName: string | null;
    documentNumber: string | null;
    personalNumber: string | null;
    dateOfIssue: string | null;
    expirationDate: string | null;
  } | null;
  liveness: { method: string | null; score: number | null; estimatedAge: number | null } | null;
  network: {
    ipCountry: string | null;
    ipCity: string | null;
    vpnOrTor: boolean | null;
    dataCenter: boolean | null;
    device: string | null;
    os: string | null;
    browser: string | null;
  } | null;
  panGiven: boolean;
}

const OPEN = ['not_started', 'in_progress', 'in_review', 'resubmitted'];
/** Checks staff can send back for a retake (apps/api/src/lib/kyc.ts requestRetake). */
const RETAKE_FROM = ['declined', 'in_review', 'abandoned', 'expired'];
/** A passed check covers cash-outs for a year (apps/api/src/lib/kyc.ts). */
export const ID_CHECK_VALID_MS = 365 * 86_400_000;

const KYC: Record<string, { label: string; tone: 'mint' | 'sun' | 'berry' | 'neutral' | 'sky' }> = {
  not_started: { label: 'Not started', tone: 'neutral' },
  in_progress: { label: 'In progress', tone: 'sky' },
  in_review: { label: 'In review', tone: 'sun' },
  resubmitted: { label: 'Retake asked', tone: 'sun' },
  approved: { label: 'Approved', tone: 'mint' },
  declined: { label: 'Declined', tone: 'berry' },
  abandoned: { label: 'Abandoned', tone: 'neutral' },
  expired: { label: 'Expired', tone: 'neutral' },
};
function KycPill({ status }: { status: string }) {
  const k = KYC[status] ?? { label: status, tone: 'neutral' as const };
  return <Pill tone={k.tone}>{k.label}</Pill>;
}

/** Where the user's identity stands, in one label. */
export function idStanding(
  check: Pick<IdCheck, 'approvedAt' | 'validUntil'> & {
    verifications: Pick<IdCheckVerification, 'status'>[];
  },
  now = Date.now(),
): { label: string; tone: 'mint' | 'sun' | 'berry' | 'neutral' } {
  if (check.approvedAt !== null && check.validUntil !== null)
    return check.validUntil > now
      ? { label: 'ID verified', tone: 'mint' }
      : { label: 'ID check expired', tone: 'neutral' };
  const latest = check.verifications[0];
  if (!latest) return { label: 'ID not checked', tone: 'neutral' };
  if (OPEN.includes(latest.status)) return { label: 'ID check in progress', tone: 'sun' };
  if (latest.status === 'declined') return { label: 'ID declined', tone: 'berry' };
  return { label: 'ID not verified', tone: 'neutral' };
}

/** "Email verified" and "ID verified" (or where each stands), side by side. */
export function VerificationPills({
  emailVerifiedAt,
  idCheck,
  onIdClick,
}: {
  emailVerifiedAt: number | null;
  idCheck: IdCheck | undefined;
  onIdClick?: () => void;
}) {
  const id = idCheck ? idStanding(idCheck) : null;
  return (
    <>
      {emailVerifiedAt ? (
        <Pill tone="mint">
          <Mail size={12} aria-hidden="true" /> Email verified
        </Pill>
      ) : (
        <Pill tone="sun">Email unverified</Pill>
      )}
      {id &&
        (onIdClick ? (
          <button
            type="button"
            onClick={onIdClick}
            className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
            title="Show identity checks"
          >
            <IdPill label={id.label} tone={id.tone} />
          </button>
        ) : (
          <IdPill label={id.label} tone={id.tone} />
        ))}
    </>
  );
}

function IdPill({ label, tone }: ReturnType<typeof idStanding>) {
  return (
    <Pill tone={tone}>
      {tone === 'mint' ? (
        <ShieldCheck size={12} aria-hidden="true" />
      ) : (
        <IdCard size={12} aria-hidden="true" />
      )}{' '}
      {label}
    </Pill>
  );
}

const FEATURE: Record<string, string> = {
  ID_VERIFICATION: 'Document',
  DATABASE_VALIDATION: 'Government database',
  LIVENESS: 'Liveness (real person)',
  FACE_MATCH: 'Face matches document',
  IP_ANALYSIS: 'Device and network',
  QUESTIONNAIRE: 'Questionnaire',
};

const score = (n: number | null) => (n === null ? '—' : `${Math.round(n)} / 100`);
const yes = (v: boolean | null) => (v === null ? '—' : v ? 'Yes' : 'No');
const dash = (v: ReactNode) => (v === null || v === undefined || v === '' ? '—' : v);
const deciderLabel = (who: string | null, names: Record<string, string>) =>
  !who ? '—' : who === 'didit' ? 'Didit (automatic)' : actorLabel(who, names);

/** The Identity tab: every check, newest first. */
export function IdChecks({
  userId,
  check,
  names,
  deleted,
}: {
  userId: string;
  check: IdCheck;
  names: Record<string, string>;
  deleted: boolean;
}) {
  const standing = idStanding(check);
  return (
    <div className="space-y-4">
      <div className="card p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <IdPill {...standing} />
          <span className="text-[14px] text-graphite">
            {check.approvedAt !== null && check.validUntil !== null ? (
              <>
                Passed <TimeAgo t={check.approvedAt} />; covers cash-outs until{' '}
                {new Date(check.validUntil).toLocaleDateString()}.
              </>
            ) : check.enabled ? (
              'Asked for before a cash-out of $25 or more (Settings → Identity checks), or when the risk rules hold one.'
            ) : (
              'Identity checks are off: Didit isn’t set up.'
            )}
          </span>
        </div>
      </div>
      {check.verifications.length === 0 ? (
        <div className="card">
          <EmptyState icon={IdCard} title="No identity check yet" />
        </div>
      ) : (
        check.verifications.map((v, i) => (
          <Verification
            key={v.id}
            userId={userId}
            v={v}
            names={names}
            deleted={deleted}
            details={check.details !== false}
            latest={i === 0}
          />
        ))
      )}
    </div>
  );
}

/** What an identity check shows to staff without users.kyc: how it went, not what it found. */
const WITHOUT_DETAILS = ['Decided', 'Updated', 'Retakes asked', 'For cash-out'];

function Verification({
  userId,
  v,
  names,
  deleted,
  details,
  latest,
}: {
  userId: string;
  v: IdCheckVerification;
  names: Record<string, string>;
  deleted: boolean;
  details: boolean;
  /** The member's newest check: the only one a retake can be asked on. */
  latest: boolean;
}) {
  const can = useCan();
  const [open, setOpen] = useState(false);
  const [retake, setRetake] = useState(false);
  const decided = v.status === 'approved' || v.status === 'declined';
  const canRetake =
    details && !deleted && latest && RETAKE_FROM.includes(v.status) && can('withdrawals.decide');
  const shown = (rows: [ReactNode, ReactNode][]) =>
    details ? rows : rows.filter(([k]) => WITHOUT_DETAILS.includes(String(k)));
  return (
    <section className="card overflow-hidden">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-hairline px-4 py-3 sm:px-5">
        <h3 className="text-[15px] font-bold">Identity check</h3>
        <KycPill status={v.status} />
        <span className="text-[13px] text-stone">
          Started <TimeAgo t={v.createdAt} />
        </span>
      </div>
      <div className="px-4 py-4 sm:px-5">
        <KV
          items={shown([
            [
              decided ? 'Decided' : 'Updated',
              <span key="d">
                <TimeAgo t={v.decidedAt ?? v.updatedAt} />
                {decided && (
                  <span className="font-normal text-stone">
                    {' '}
                    by {deciderLabel(v.decidedBy, names)}
                  </span>
                )}
              </span>,
            ],
            [
              'Document',
              dash([v.documentType, v.documentCountry].filter(Boolean).join(', ') || null),
            ],
            ['Liveness', score(v.livenessScore)],
            ['Face match', score(v.faceMatchScore)],
            [
              'Risk codes',
              v.warnings?.length ? (
                <span key="w" className="flex flex-wrap gap-1.5">
                  {v.warnings.map((w) => (
                    <Pill key={w} tone="sun">
                      {humanize(w.toLowerCase())}
                    </Pill>
                  ))}
                </span>
              ) : (
                'None'
              ),
            ],
            ...(v.resubmissions > 0
              ? ([['Retakes asked', String(v.resubmissions)]] as [ReactNode, ReactNode][])
              : []),
            ...(v.userMessage
              ? ([['Told the user', v.userMessage]] as [ReactNode, ReactNode][])
              : []),
            [
              'Same document',
              v.sameDocument.length ? (
                <span key="s" className="flex flex-wrap gap-x-3 gap-y-1">
                  {v.sameDocument.map((s) => (
                    <Link
                      key={s.userId}
                      to={`/users/${s.userId}`}
                      className="text-berry-deep underline decoration-2 underline-offset-4"
                    >
                      {s.username}
                    </Link>
                  ))}
                </span>
              ) : (
                'No other account'
              ),
            ],
            ...(v.withdrawalId
              ? ([['For cash-out', <Mono key="c">{v.withdrawalId}</Mono>]] as [
                  ReactNode,
                  ReactNode,
                ][])
              : []),
            ['Didit session', <Mono key="m">{v.diditSessionId ?? ''}</Mono>],
          ])}
        />
      </div>
      {!details ? (
        <div className="border-t border-hairline px-4 py-3 sm:px-5">
          <ViewOnly>
            The document, scores and risk codes need the “See identity check details” permission.
          </ViewOnly>
        </div>
      ) : deleted ? null : open ? (
        <DocumentDetails
          userId={userId}
          kycId={v.id}
          retake={canRetake ? (retake ? 'open' : 'offer') : 'none'}
          onRetake={setRetake}
        />
      ) : (
        <div className="border-t border-hairline px-4 py-3 sm:px-5">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-ghost h-10 gap-2 px-4 py-0 text-[14px]"
              onClick={() => setOpen(true)}
            >
              <ScanFace size={15} aria-hidden="true" />
              Show what the document says
            </button>
            {canRetake && (
              <button
                type="button"
                className="btn-ghost h-10 gap-2 px-4 py-0 text-[14px]"
                onClick={() => {
                  setRetake(true);
                  setOpen(true);
                }}
              >
                <RotateCcw size={15} aria-hidden="true" />
                Ask for a retake
              </button>
            )}
          </div>
          <p className="mt-2 text-[12px] text-stone">
            Read from Didit now: name, date of birth, masked document number and the checks it ran.
            Not saved here; the view is logged.
            {canRetake &&
              ' A retake sends the member back to the steps you choose, like Didit’s console does; a declined check then stops counting as a decline.'}
          </p>
        </div>
      )}
    </section>
  );
}

function DocumentDetails({
  userId,
  kycId,
  retake,
  onRetake,
}: {
  userId: string;
  kycId: string;
  /** Whether a retake may be asked on this check, and whether its panel is open. */
  retake: 'none' | 'offer' | 'open';
  onRetake: (open: boolean) => void;
}) {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['kyc-details', kycId],
    queryFn: () =>
      api<{ details: Details; readAt: number }>(`/users/${userId}/kyc/${kycId}/details`, {
        body: {},
      }),
    // Each read calls Didit and is audited; personal data isn't kept once the panel closes.
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
  if (isLoading)
    return (
      <div className="border-t border-hairline">
        <SkeletonRows rows={4} />
      </div>
    );
  if (error || !data)
    return (
      <div className="border-t border-hairline px-4 py-3 sm:px-5">
        <ErrorNote error={error ?? new Error('No answer.')} onRetry={() => void refetch()} />
      </div>
    );
  const d = data.details;
  const doc = d.document;
  const name = doc?.fullName ?? ([doc?.firstName, doc?.lastName].filter(Boolean).join(' ') || null);
  const net = d.network;
  return (
    <div className="border-t border-hairline bg-canvas/60 px-4 py-4 sm:px-5">
      <div className="mb-3 flex flex-wrap items-baseline gap-x-2 text-[13px] text-stone">
        <span className="font-bold text-ink">From the document</span>
        <span>
          · read from Didit <TimeAgo t={data.readAt} />, not saved
        </span>
      </div>
      {doc ? (
        <KV
          items={[
            ['Name', dash(name)],
            [
              'Date of birth',
              dash(
                doc.dateOfBirth
                  ? `${doc.dateOfBirth}${doc.age !== null ? ` (age ${doc.age})` : ''}`
                  : null,
              ),
            ],
            ['Gender', dash(doc.gender)],
            ['Nationality', dash(doc.nationality)],
            ['Issued by', dash(doc.issuingCountryName ?? d.documentCountry)],
            ['Document type', dash(d.documentType)],
            [
              'Document number',
              doc.documentNumber ? <Mono key="n">{doc.documentNumber}</Mono> : '—',
            ],
            ...(doc.personalNumber
              ? ([['Personal number', <Mono key="p">{doc.personalNumber}</Mono>]] as [
                  ReactNode,
                  ReactNode,
                ][])
              : []),
            ['Issued', dash(doc.dateOfIssue)],
            ['Expires', dash(doc.expirationDate)],
            ...(d.documentCountry === 'IND'
              ? ([['PAN given', d.panGiven ? 'Yes (not shown)' : 'No']] as [ReactNode, ReactNode][])
              : []),
          ]}
        />
      ) : (
        <p className="text-[14px] text-stone">The document step has no data yet.</p>
      )}

      <div className="mb-3 mt-5 text-[13px] font-bold">What was checked</div>
      {d.steps.length ? (
        <ul className="divide-y divide-hairline rounded-[12px] border border-hairline bg-paper">
          {d.steps.map((s, i) => (
            <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5">
              <span className="min-w-[10rem] text-[14px] font-semibold">
                {FEATURE[s.feature] ?? humanize(s.feature.toLowerCase())}
              </span>
              {s.status && <KycPill status={s.status} />}
              {s.score !== null && (
                <span className="text-[13px] tabular-nums text-stone">{score(s.score)}</span>
              )}
              {s.matches ? (
                <Pill tone="berry">
                  {s.matches} match{s.matches === 1 ? '' : 'es'} with other people
                </Pill>
              ) : null}
              {s.warnings.map((w) => (
                <Pill key={w.risk} tone={w.level === 'error' ? 'berry' : 'sun'}>
                  {humanize(w.risk.toLowerCase())}
                </Pill>
              ))}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-stone">No steps finished yet.</p>
      )}
      {retake === 'open' ? (
        <RetakePanel
          userId={userId}
          kycId={kycId}
          steps={d.steps}
          onClose={() => onRetake(false)}
        />
      ) : retake === 'offer' && d.steps.length ? (
        <button
          type="button"
          className="btn-ghost mt-3 h-10 gap-2 px-4 py-0 text-[14px]"
          onClick={() => onRetake(true)}
        >
          <RotateCcw size={15} aria-hidden="true" />
          Ask for a retake
        </button>
      ) : null}

      {(d.liveness || net) && (
        <div className="mt-5">
          <KV
            items={[
              ...(d.liveness
                ? ([
                    [
                      'Liveness',
                      `${score(d.liveness.score)}${d.liveness.method ? ` · ${humanize(d.liveness.method.toLowerCase())}` : ''}${d.liveness.estimatedAge !== null ? ` · looks about ${d.liveness.estimatedAge}` : ''}`,
                    ],
                  ] as [ReactNode, ReactNode][])
                : []),
              ...(net
                ? ([
                    [
                      'Network at check',
                      dash([net.ipCity, net.ipCountry].filter(Boolean).join(', ') || null),
                    ],
                    ['VPN or Tor', yes(net.vpnOrTor)],
                    ['Data centre IP', yes(net.dataCenter)],
                    [
                      'Device',
                      dash([net.device, net.os, net.browser].filter(Boolean).join(' · ') || null),
                    ],
                  ] as [ReactNode, ReactNode][])
                : []),
            ]}
          />
        </div>
      )}
      <p className="mt-4 text-[12px] text-stone">
        Photos, address and full numbers stay with Didit. Open the session in Didit’s console if you
        need them.
      </p>
    </div>
  );
}

/** Steps a retake can name, in the order Didit's console lists them; the last two re-run by themselves. */
const RETAKE_STEPS: { feature: string; label: string; backend?: boolean }[] = [
  { feature: 'ID_VERIFICATION', label: 'Document photos' },
  { feature: 'LIVENESS', label: 'Selfie (liveness)' },
  { feature: 'FACE_MATCH', label: 'Face match' },
  { feature: 'DATABASE_VALIDATION', label: 'Government database', backend: true },
  { feature: 'IP_ANALYSIS', label: 'Device and network', backend: true },
];
type Advice = 'document' | 'selfie' | 'both' | 'generic' | 'custom';
/** The advice that fits the steps chosen; staff can pick another. */
function adviceFor(steps: string[]): Advice {
  const doc = steps.includes('ID_VERIFICATION');
  const selfie = steps.includes('LIVENESS') || steps.includes('FACE_MATCH');
  return doc && selfie ? 'both' : doc ? 'document' : selfie ? 'selfie' : 'generic';
}
/** The English of each ready-made message (apps/api/src/lib/kyc.ts RETAKE_ADVICE); the member reads it in their language. */
const ADVICE: { id: Advice; text: string }[] = [
  {
    id: 'document',
    text: 'Please photograph your original ID again: the whole card, both sides, in good light.',
  },
  {
    id: 'selfie',
    text: 'Please retake your selfie: face the light, no glasses or hat, nobody else in the picture.',
  },
  { id: 'both', text: 'Both of the above' },
  { id: 'generic', text: 'Please retake the steps we asked for.' },
  { id: 'custom', text: 'My own words (shown as written, in English)' },
];

/**
 * Like Didit's "Request resubmission": pick the steps the member redoes and what they are told.
 * Steps that didn't pass start ticked.
 */
function RetakePanel({
  userId,
  kycId,
  steps,
  onClose,
}: {
  userId: string;
  kycId: string;
  steps: Step[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const present = RETAKE_STEPS.filter((r) => steps.some((s) => s.feature === r.feature));
  const [chosen, setChosen] = useState<string[]>(() =>
    present
      .filter((r) => steps.find((s) => s.feature === r.feature)?.status !== 'approved')
      .map((r) => r.feature),
  );
  const [advice, setAdvice] = useState<Advice | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const userSteps = chosen.some((f) => !RETAKE_STEPS.find((r) => r.feature === f)?.backend);
  const picked = advice ?? adviceFor(chosen);
  const toggle = (feature: string, on: boolean) =>
    setChosen((c) => (on ? [...c, feature] : c.filter((f) => f !== feature)));
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ userAction: boolean; waitingCashouts: number }>(
        `/users/${userId}/kyc/${kycId}/retake`,
        {
          body: {
            steps: chosen,
            advice: picked,
            ...(picked === 'custom' ? { note: note.trim() } : {}),
          },
        },
      );
      toast(
        !r.userAction
          ? 'Re-run asked. Didit runs it now; the result arrives in a minute.'
          : r.waitingCashouts
            ? 'Retake asked. The member sees “Retake” on Earn and under Cash out, and got a notification.'
            : 'Retake asked. No cash-out is waiting, so the member sees “Retake” when they next request one.',
      );
      await qc.invalidateQueries({ queryKey: ['user', userId] });
      onClose();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 rounded-[12px] border border-ink/20 bg-paper p-4">
      <div className="flex items-center gap-2 text-[14px] font-bold">
        <RotateCcw size={15} aria-hidden="true" /> Ask for a retake
      </div>
      <p className="mt-1 text-[13px] text-stone">
        The member redoes the ticked steps with the same link; the rest of the check stands. Didit
        decides again; an In Review goes to the automatic reviewer.
      </p>
      {!present.length && (
        <p className="mt-3 text-[14px] text-stone">This check has no steps to send back yet.</p>
      )}
      <ul className="mt-3 divide-y divide-hairline rounded-[10px] border border-hairline">
        {present.map((r) => {
          const s = steps.find((x) => x.feature === r.feature)!;
          return (
            <li key={r.feature} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
              <label className="flex min-w-[12rem] items-center gap-2 text-[14px]">
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 accent-ink"
                  checked={chosen.includes(r.feature)}
                  onChange={(e) => toggle(r.feature, e.target.checked)}
                />
                {r.label}
              </label>
              {s.status && <KycPill status={s.status} />}
              {r.backend && (
                <span className="text-[12px] text-stone">re-runs by itself, nothing to do</span>
              )}
            </li>
          );
        })}
      </ul>
      {userSteps && (
        <fieldset className="mt-3">
          <legend className="text-[13px] font-bold">What the member reads</legend>
          <div className="mt-1.5 space-y-1.5">
            {ADVICE.map((a) => (
              <label key={a.id} className="flex items-start gap-2 text-[14px]">
                <input
                  type="radio"
                  name={`advice-${kycId}`}
                  className="mt-1 h-4 w-4 shrink-0 accent-ink"
                  checked={picked === a.id}
                  onChange={() => setAdvice(a.id)}
                />
                <span>{a.text}</span>
              </label>
            ))}
          </div>
          {picked === 'custom' && (
            <textarea
              className="input mt-2 min-h-[72px] w-full text-[14px]"
              maxLength={300}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What they should do differently, in one or two sentences"
            />
          )}
        </fieldset>
      )}
      {error ? (
        <div className="mt-3">
          <ErrorNote error={error} />
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary h-10 px-4 py-0 text-[14px]"
          disabled={busy || !chosen.length || (picked === 'custom' && note.trim().length < 3)}
          onClick={() => void submit()}
        >
          Ask for a retake
        </button>
        <button
          type="button"
          className="btn-ghost h-10 px-4 py-0 text-[14px]"
          disabled={busy}
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
